import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
} from '@mui/material';
import { CreateNotificationPayload, Notification } from '@core/interfaces/api';

export interface NotificationModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateNotificationPayload & { id?: number }) => Promise<void> | void;
  siteId: number;
  initial?: Notification | null;
}

function endOfDayISOString(): string {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 0, 0);
  return end.toISOString();
}

export const NotificationModal: React.FC<NotificationModalProps> = ({
  open,
  onClose,
  onSubmit,
  siteId,
  initial,
}) => {
  const isEditing = Boolean(initial?.id);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [moreInfoUrl, setMoreInfoUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initial) {
      setTitle(initial.title || '');
      setText(initial.text || '');
      setMoreInfoUrl(initial.moreInfoUrl || '');
    } else if (open) {
      setTitle('');
      setText('');
      setMoreInfoUrl('');
    }
  }, [initial, open]);

  const isValidUrl = useMemo(() => {
    if (!moreInfoUrl) return true;
    try {
      // Allow http/https and simple URLs
      const url = new URL(moreInfoUrl);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (e) {
      return false;
    }
  }, [moreInfoUrl]);

  const handleSubmit = async () => {
    if (!title?.trim() || !text?.trim()) return;
    if (!isValidUrl) return;

    const payload: CreateNotificationPayload & { id?: number } = {
      title: title.trim(),
      text: text.trim(),
      moreInfoUrl: moreInfoUrl?.trim() || '',
      siteId,
      // keep parity with Angular form defaults
      expiration: initial?.expiration || endOfDayISOString(),
    };

    if (initial?.id) payload.id = initial.id;

    try {
      setSubmitting(true);
      await onSubmit(payload);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEditing ? 'Edit Community Update' : 'Send Community Update'}
      </DialogTitle>
      <DialogContent dividers>
        {!isEditing ? (
          <p className="italic mb-2">
            This Community Update will be sent to any residents' mobile phones and tablets with the GiGi app installed. It will also appear in the app's Community Updates tab.
          </p>
        ) : (
          <p className="italic mb-2">
            This will update the Community Update within the app. The push notification has already been sent and cannot be edited. This will not send a new push notification.
          </p>
        )}
        <div className="flex flex-col gap-4">
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            inputProps={{ maxLength: 35 }}
            helperText={`${title.length}/35 (optimal character count is 26 or less)`}
          />
          <TextField
            label="Text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            required
            helperText={`${text.length}/140 characters`}
          />
          <TextField
            label="Link for Additional Information (optional)"
            value={moreInfoUrl}
            onChange={(e) => setMoreInfoUrl(e.target.value)}
            type="url"
            error={Boolean(moreInfoUrl) && !isValidUrl}
            helperText={
              Boolean(moreInfoUrl) && !isValidUrl
                ? 'Please insert valid website link (e.g. https://www.thegoodmangroup.com)'
                : undefined
            }
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || !title?.trim() || !text?.trim() || !isValidUrl}
          variant="contained"
          color="primary"
        >
          {submitting ? <CircularProgress size={18} /> : isEditing ? 'Update' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

